import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Loader2, Copy, ExternalLink, ShieldOff, QrCode, Ticket as TicketIcon } from "lucide-react";
import { toast } from "sonner";
import { eventsApi, scannerTokensApi, formatAuDate } from "../../lib/events";
import { extractError } from "../../lib/api";

export default function ScannerTokens() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [ev, list] = await Promise.all([eventsApi.get(id), scannerTokensApi.list(id)]);
      setEvent(ev);
      setTokens(list);
    } catch (e) { setError(extractError(e)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const onCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const t = await scannerTokensApi.create(id, { label: label.trim() });
      setTokens((prev) => [t, ...(prev || [])]);
      setLabel("");
      toast.success("Scanner link created. Copy and share with door staff.");
    } catch (err) { toast.error(extractError(err)); }
    finally { setCreating(false); }
  };

  const onRevoke = async (tokenId) => {
    if (!window.confirm("Revoke this scanner link? It cannot be undone.")) return;
    try {
      await scannerTokensApi.revoke(id, tokenId);
      setTokens((prev) => (prev || []).map((t) => t.id === tokenId ? { ...t, revoked: true } : t));
      toast.success("Scanner link revoked.");
    } catch (err) { toast.error(extractError(err)); }
  };

  const copy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Scanner link copied");
  };

  const loading = event === null || tokens === null;

  return (
    <section className="p-6 sm:p-10" data-testid="scanner-tokens-page">
      <div className="max-w-4xl mx-auto">
        <Link to={`/dashboard/events/${id}/edit`} className="gt-btn-ghost -ml-4 mb-2 text-sm" data-testid="back-to-event">
          <ArrowLeft size={14} /> Back to event
        </Link>
        <div className="gt-label mb-2">Door scanners</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
          Scanner links
        </h2>
        <p className="text-[var(--gt-ink-2)] text-sm mb-8 max-w-2xl">
          Generate a unique scanner link per door or staff member. Anyone with a link can scan tickets for this event only. Links are never exposed to buyers and can be revoked at any time.
        </p>

        {loading && (
          <div className="flex items-center gap-3 text-[var(--gt-ink-2)]" data-testid="scanner-tokens-loading">
            <Loader2 size={16} className="animate-spin" /><span className="text-sm">Loading…</span>
          </div>
        )}

        {error && (
          <div className="gt-card border-[var(--gt-accent)] text-[var(--gt-accent)] mb-6" data-testid="scanner-tokens-error">{error}</div>
        )}

        {!loading && (
          <>
            <form onSubmit={onCreate} className="gt-card mb-8" data-testid="create-scanner-token-form">
              <div className="gt-label mb-2">Create a new scanner link</div>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  className="gt-input flex-1"
                  placeholder="Label (e.g. 'Main door', 'Backstage', 'Sam K')"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  maxLength={80}
                  data-testid="scanner-token-label-input"
                />
                <button type="submit" className="gt-btn-primary" disabled={creating} data-testid="create-scanner-token-btn">
                  {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Generate link
                </button>
              </div>
              <p className="mt-3 text-xs text-[var(--gt-ink-3)]">
                {event?.end_at ? `Default expiry: 12h after the event ends (${formatAuDate(event.end_at)}).` : "Default expiry: 12h after the event start."}
              </p>
            </form>

            <div className="space-y-3" data-testid="scanner-tokens-list">
              {(tokens || []).length === 0 && (
                <div className="border-2 border-dashed border-[var(--gt-line)] p-8 text-center text-sm text-[var(--gt-ink-3)]" data-testid="scanner-tokens-empty">
                  No scanner links yet. Create one above.
                </div>
              )}
              {(tokens || []).map((t) => (
                <div
                  key={t.id}
                  className={`gt-card ${t.revoked ? "opacity-60" : ""}`}
                  data-testid={`scanner-token-row-${t.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <QrCode size={14} className="text-[var(--gt-ink-2)]" />
                        <span className="font-semibold" data-testid={`scanner-token-label-${t.id}`}>{t.label}</span>
                        {t.revoked && (
                          <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-sm bg-[var(--gt-accent)] text-white" data-testid={`scanner-token-revoked-${t.id}`}>
                            Revoked
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[var(--gt-ink-3)] mb-2">
                        Created {formatAuDate(t.created_at)}
                        {t.expires_at && <> · expires {formatAuDate(t.expires_at)}</>}
                        {t.last_used_at && <> · last used {formatAuDate(t.last_used_at)}</>}
                        <> · <span className="font-mono">{t.scan_count}</span> scan{t.scan_count === 1 ? "" : "s"}</>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <code
                          className="font-mono text-[11px] bg-[var(--gt-bg-surface)] px-2 py-1 rounded-sm break-all"
                          data-testid={`scanner-token-url-${t.id}`}
                        >
                          {t.url}
                        </code>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2">
                      <button
                        type="button"
                        onClick={() => copy(t.url)}
                        className="gt-btn-ghost text-xs"
                        disabled={t.revoked}
                        data-testid={`copy-scanner-token-${t.id}`}
                      >
                        <Copy size={12} /> Copy
                      </button>
                      <a
                        href={t.url}
                        target="_blank"
                        rel="noreferrer"
                        className="gt-btn-ghost text-xs"
                        data-testid={`open-scanner-token-${t.id}`}
                      >
                        <ExternalLink size={12} /> Open
                      </a>
                      {!t.revoked && (
                        <button
                          type="button"
                          onClick={() => onRevoke(t.id)}
                          className="text-xs text-[var(--gt-accent)] hover:underline inline-flex items-center gap-1"
                          data-testid={`revoke-scanner-token-${t.id}`}
                        >
                          <ShieldOff size={12} /> Revoke
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
